import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { ChatSidebar } from '@/components/chat/ChatSidebar';
import { MessageThread } from '@/components/chat/MessageThread';
import { MessageComposer } from '@/components/chat/MessageComposer';
import { SessionQuickActionPanel } from '@/components/chat/SessionQuickActionPanel';
import { ConfirmedSessionsPanel } from '@/components/chat/ConfirmedSessionsPanel';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useChannels, useMessages, useSendMessage, useCreateChannel, useUserSearch, useMarkChannelAsRead } from '@/hooks/useChat';
import { ChatMessage, ChatUserResult, chatApi } from '@/api/chat';
import { X, MessageSquare, ChevronLeft, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useChatPanel } from '@/contexts/ChatPanelContext';

export function ChatPanel() {
  const { isOpen, closeChat, selectedChannelId, setSelectedChannelId } = useChatPanel();
  const queryClient = useQueryClient();
  useAuth(); // Keep auth context active
  // `inert` removes the closed panel from the tab order and the accessibility tree.
  // Set through the DOM because React 18 does not forward the attribute.
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (isOpen) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  }, [isOpen]);
  const { data: channels, isLoading: channelsLoading } = useChannels();
  const messagesQuery = useMessages(selectedChannelId || undefined);
  const sendMutation = useSendMessage(selectedChannelId || undefined);
  const createChannelMutation = useCreateChannel();
  const markAsReadMutation = useMarkChannelAsRead();
  const [searchTerm, setSearchTerm] = useState('');
  const { data: searchResults, isFetching: searching } = useUserSearch(searchTerm);
  const [newMessage, setNewMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pendingGroupUsers, setPendingGroupUsers] = useState<ChatUserResult[]>([]);
  const lastSeenMessageTimeRef = useRef<string | null>(null);
  const markAsReadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Flatten all pages of messages
  const messages = messagesQuery.data?.pages 
    ? (messagesQuery.data.pages.slice().reverse().flatMap((page: ChatMessage[]) => page) as ChatMessage[])
    : [];

  // Get current channel's lastReadAt
  const currentChannel = channels?.find(ch => ch.id === selectedChannelId);
  const lastReadAt = currentChannel?.lastReadAt;

  // Handle message visibility - debounced mark as read
  const handleMessageVisible = useCallback((_messageId: string, messageTime: string) => {
    if (!lastSeenMessageTimeRef.current || 
        new Date(messageTime).getTime() > new Date(lastSeenMessageTimeRef.current).getTime()) {
      lastSeenMessageTimeRef.current = messageTime;

      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
      }

      markAsReadTimeoutRef.current = setTimeout(() => {
        if (selectedChannelId) {
          markAsReadMutation.mutate(selectedChannelId);
        }
      }, 500);
    }
  }, [selectedChannelId, markAsReadMutation]);

  // Reset seen message tracking when channel changes
  useEffect(() => {
    lastSeenMessageTimeRef.current = null;
    if (markAsReadTimeoutRef.current) {
      clearTimeout(markAsReadTimeoutRef.current);
    }
  }, [selectedChannelId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current);
      }
    };
  }, []);

  const sortedChannels = useMemo(() => {
    if (!channels) return [];
    return [...channels].sort((a, b) => {
      const aLastMessage = a.lastMessageAt || a.createdAt;
      const bLastMessage = b.lastMessageAt || b.createdAt;
      return new Date(bLastMessage).getTime() - new Date(aLastMessage).getTime();
    });
  }, [channels]);

  const sortedSearchResults = useMemo(() => {
    if (!searchTerm.trim() || searchTerm.trim().length < 2) {
      return { channels: sortedChannels, users: [] };
    }

    const searchLower = searchTerm.toLowerCase();
    
    const sortedChannelsByRelevance = [...sortedChannels].sort((a, b) => {
      const aName = a.name?.toLowerCase() || '';
      const bName = b.name?.toLowerCase() || '';
      const aMatches = aName.includes(searchLower);
      const bMatches = bName.includes(searchLower);
      
      if (aMatches && !bMatches) return -1;
      if (!aMatches && bMatches) return 1;
      return 0;
    });

    return {
      channels: sortedChannelsByRelevance,
      users: searchResults || []
    };
  }, [searchTerm, sortedChannels, searchResults]);

  const handleSelectChannel = (channelId: string) => {
    setSelectedChannelId(channelId);
    setSearchTerm('');
    setPendingGroupUsers([]);
  };

  const handleAddUserToGroup = async (targetUser: ChatUserResult) => {
    // Always add to pending group first - user can click to start DM or add more for group
    if (!pendingGroupUsers.find(u => u.id === targetUser.id)) {
      setPendingGroupUsers([...pendingGroupUsers, targetUser]);
    }
    setSearchTerm('');
  };

  const handleStartDirectMessage = async () => {
    if (pendingGroupUsers.length !== 1) return;
    
    const targetUser = pendingGroupUsers[0];
    try {
      const channel = await chatApi.createChannel({
        type: 'PRIVATE',
        memberIds: [targetUser.id],
      });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      setSelectedChannelId(channel.id);
      setPendingGroupUsers([]);
      setSearchTerm('');
    } catch (error) {
      console.error('Failed to create channel:', error);
    }
  };

  const handleRemoveUserFromGroup = (userId: string) => {
    setPendingGroupUsers(pendingGroupUsers.filter(u => u.id !== userId));
  };

  const handleCreateGroup = async () => {
    if (pendingGroupUsers.length < 1) return;

    try {
      const memberIds = pendingGroupUsers.map(u => u.id);
      const channel = await createChannelMutation.mutateAsync({
        type: 'GROUP',
        memberIds,
      });
      setSelectedChannelId(channel.id);
      setPendingGroupUsers([]);
      setSearchTerm('');
    } catch (error) {
      console.error('Failed to create group:', error);
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedChannelId) return;

    try {
      if (selectedFile) {
        await chatApi.uploadFile(selectedChannelId, selectedFile, newMessage.trim() || undefined);
      } else {
        await sendMutation.mutateAsync(newMessage.trim());
      }
      setNewMessage('');
      setSelectedFile(null);
      queryClient.invalidateQueries({ queryKey: ['messages', selectedChannelId] });
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleLoadMore = () => {
    if (messagesQuery.hasNextPage && !messagesQuery.isFetchingNextPage) {
      messagesQuery.fetchNextPage();
    }
  };

  // Get channel name for header
  const getChannelDisplayName = () => {
    if (!currentChannel) return 'Chat';
    if (currentChannel.type === 'GROUP') {
      return currentChannel.name || 'Group Chat';
    }
    // For private chats, use the channel name or last message sender
    return currentChannel.name || currentChannel.lastMessageSenderName || 'Chat';
  };

  return (
    <>
      {/* Chat Panel */}
      <div 
        // Closed: hidden for real (not just translated offscreen) so it cannot
        // widen the page at 390px or take focus. `inert` blocks tab/AT access.
        aria-hidden={!isOpen}
        ref={panelRef}
        data-testid="chat-panel"
        className={`fixed top-0 right-0 h-full w-[380px] max-w-full bg-background border-l shadow-xl z-50 flex flex-col transition-all duration-300 ease-in-out ${
          isOpen ? 'translate-x-0 visible' : 'translate-x-full invisible'
        }`}
      >
        {/* Panel Header */}
        <div className="flex items-center justify-between p-3 border-b bg-background">
          <div className="flex items-center gap-2">
            {selectedChannelId && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => setSelectedChannelId(null)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <MessageSquare className="h-5 w-5 text-foreground" />
            <span className="font-semibold">
              {selectedChannelId ? getChannelDisplayName() : 'Messages'}
            </span>
            {/* Monitored indicator - subtle shield icon with tooltip */}
            {selectedChannelId && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground/50 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[200px] text-center">
                    <p>Monitored by Mentara for safety</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 w-8 p-0"
            onClick={closeChat}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content - Chat List (hidden when channel selected) */}
        <div className={`flex-1 overflow-hidden flex flex-col ${selectedChannelId ? 'hidden' : ''}`}>
          <ChatSidebar
            channels={sortedSearchResults.channels}
            channelsLoading={channelsLoading}
            selectedChannelId={selectedChannelId || undefined}
            onSelectChannel={handleSelectChannel}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            searchResults={sortedSearchResults.users}
            searching={searching}
            pendingGroupUsers={pendingGroupUsers}
            onAddUserToGroup={handleAddUserToGroup}
            onRemoveUserFromGroup={handleRemoveUserFromGroup}
          />
        </div>

        {/* Message Thread View (shown when channel selected) */}
        {selectedChannelId && (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Confirmed sessions panel */}
            {currentChannel?.confirmedSessions && currentChannel.confirmedSessions.length > 0 && (
              <ConfirmedSessionsPanel
                confirmedSessions={currentChannel.confirmedSessions}
              />
            )}
            
            {/* Quick action panel for pending sessions */}
            {currentChannel?.pendingSessions && currentChannel.pendingSessions.length > 0 && (
              <SessionQuickActionPanel
                pendingSessions={currentChannel.pendingSessions}
                onActionComplete={() => {
                  queryClient.invalidateQueries({ queryKey: ['chat', 'channels'] });
                }}
              />
            )}
            <MessageThread
              selectedChannelId={selectedChannelId}
              messages={messages}
              lastReadAt={lastReadAt}
              onMessageVisible={handleMessageVisible}
              onLoadMore={handleLoadMore}
              hasMore={messagesQuery.hasNextPage}
              isLoadingMore={messagesQuery.isFetchingNextPage}
            />
            <MessageComposer
              value={newMessage}
              onChange={setNewMessage}
              onSend={handleSendMessage}
              onFileSelect={setSelectedFile}
              disabled={sendMutation.isPending}
              hasChannel={!!selectedChannelId}
              selectedFile={selectedFile}
              onClearFile={() => setSelectedFile(null)}
            />
          </div>
        )}

        {/* Create Group Button */}
        {pendingGroupUsers.length >= 2 && !selectedChannelId && (
          <div className="p-3 border-t">
            <Button 
              className="w-full" 
              onClick={handleCreateGroup}
              disabled={createChannelMutation.isPending}
            >
              Create Group ({pendingGroupUsers.length} members)
            </Button>
          </div>
        )}

        {/* Start DM Button - when exactly 1 user selected */}
        {pendingGroupUsers.length === 1 && !selectedChannelId && (
          <div className="p-3 border-t space-y-2">
            <Button 
              className="w-full" 
              onClick={handleStartDirectMessage}
            >
              Start Chat with {pendingGroupUsers[0].fullName}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Or search for more users to create a group
            </p>
          </div>
        )}
      </div>
    </>
  );
}
